/**
 * Global state store via Zustand.
 * Minimal state — only what needs to be shared across components.
 * API data is managed by TanStack Query (not stored here).
 */

import { create } from "zustand";
import type { UserData, AccountData } from "./api";

type ToastType = "error" | "success" | "info";

interface Toast {
  id: string;
  type: ToastType;
  title?: string;
  message: string;
}

interface NotifyOptions {
  type: ToastType;
  title?: string;
  message: string;
  duration?: number;
}

interface AppState {
  // Auth
  user: UserData | null;
  setUser: (user: UserData | null) => void;

  // Active account (for multi-account)
  activeAccountId: string | null;
  setActiveAccountId: (id: string) => void;

  // Derived
  activeAccount: () => AccountData | null;
  isPro: () => boolean;

  // Toasts
  toasts: Toast[];
  notify: (options: NotifyOptions) => void;
  dismissToast: (id: string) => void;
}

export const useStore = create<AppState>((set, get) => ({
  user: null,
  setUser: (user) => {
    set({ user });
    // Auto-select primary account if none selected
    if (user && !get().activeAccountId) {
      const primary = user.accounts.find((a) => a.isPrimary);
      set({ activeAccountId: primary?.id || user.accounts[0]?.id || null });
    }
  },

  activeAccountId: null,
  setActiveAccountId: (id) => set({ activeAccountId: id }),

  activeAccount: () => {
    const { user, activeAccountId } = get();
    if (!user || !activeAccountId) return null;
    return user.accounts.find((a) => a.id === activeAccountId) || null;
  },

  isPro: () => get().user?.subscriptionStatus === "pro",

  toasts: [],
  notify: ({ type, title, message, duration = 5000 }) => {
    const id = crypto.randomUUID();
    set((state) => ({
      toasts: [...state.toasts, { id, type, title, message }],
    }));

    window.setTimeout(() => {
      get().dismissToast(id);
    }, duration);
  },
  dismissToast: (id) =>
    set((state) => ({
      toasts: state.toasts.filter((toast) => toast.id !== id),
    })),
}));
